import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import HeatmapRenderer from '@arcgis/core/renderers/HeatmapRenderer';
import {
  finalize,
  from,
  mergeMap,
  Subject,
  Subscription,
  tap,
  zip,
} from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { EsriUtils } from 'app/shared/utils/esri.utils';
import { TocService } from '../../toc.service';
import { TranslationService } from '@services/translation.service';

@Component({
  selector: 'app-heatmap-layers',
  templateUrl: 'heatmap-layers.component.html',
  styleUrls: ['heatmap-layers.component.scss'],
})
export class HeatmapLayersComponent implements OnChanges, OnDestroy {
  @Input()
  mapView: __esri.MapView;
  @Input()
  manipulationGroupLayer: __esri.GroupLayer;

  loadingController = new LoadingController();

  onLayerEnabledSubject = new Subject<any>();

  private mapViewSubscription: Subscription;

  constructor(
    private mainMapService: MainMapService,
    private tocService: TocService,
    public translationService: TranslationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mapView || changes.manipulationGroupLayer) {
      this.handleMapView();
    }
  }

  ngOnDestroy(): void {
    this.mapViewSubscription?.unsubscribe();
  }

  allowLayerFn = (layer: __esri.FeatureLayer | __esri.Sublayer): boolean => {
    return this.tocService.canShowInHeatmap(layer);
  };

  private handleMapView() {
    this.mapViewSubscription?.unsubscribe();
    if (!this.mapView) {
      return;
    }

    const subscription = new Subscription();

    const groupLayer: __esri.LayersMixin =
      this.manipulationGroupLayer ?? this.mapView.map;

    let layer: __esri.FeatureLayer;
    let layerSubscription: Subscription;

    let mainImageLayerShouldInvisible = false;

    const handleMainImageLayerVisible = (shouldEnableEffect = false) => {
      if (!mainImageLayerShouldInvisible && shouldEnableEffect) {
        mainImageLayerShouldInvisible = true;
        this.mainMapService.hideMainImageLayer();
      } else if (mainImageLayerShouldInvisible && !shouldEnableEffect) {
        mainImageLayerShouldInvisible = false;
        this.mainMapService.showMainImageLayer();
      }
    };

    const doDisable = () => {
      if (layer) {
        layerSubscription?.unsubscribe();
        this.loadingController.hide();
      }

      handleMainImageLayerVisible();
    };

    let currentUrl: string;
    subscription.add(
      this.onLayerEnabledSubject.subscribe((e) => {
        if (e) {
          const sourceLayer = e.layerItem.data.layer;
          const layerId: number = sourceLayer.layerId ?? sourceLayer.id;
          let url = EsriUtils.resolveLayerUrl(sourceLayer.url, layerId);

          let renderer: HeatmapRenderer;
          if (!layer || currentUrl !== url) {
            doDisable();

            currentUrl = url;
            layer = new FeatureLayer({
              url,
              maxScale: 0,
              minScale: 300000,
            });

            renderer = new HeatmapRenderer({
              colorStops: [
                { ratio: 0, color: 'rgba(255, 255, 255,0)' },
                { ratio: 0.1, color: 'rgba(134, 194, 201, 0.9)' },
                { ratio: 0.2, color: 'rgba(144, 160, 190, 0.9)' },
                { ratio: 0.3, color: 'rgba(156, 130, 133, 0.9)' },
                { ratio: 0.4, color: 'rgba(163, 108, 157, 0.9)' },
                { ratio: 0.5, color: 'rgba(179, 61, 106, 0.9)' },
                { ratio: 0.6, color: 'rgba(196, 13, 23, 0.9)' },
                { ratio: 0.7, color: 'rgba(219, 86, 0, 0.9)' },
                { ratio: 0.8, color: 'rgba(229,140,0, 0.9)' },
                { ratio: 1, color: 'rgba(251,239,0, 0.9)' },
              ],
            });
            layer.renderer = renderer;

            this.loadingController.show();
            layerSubscription = new Subscription(() => {
              groupLayer.remove(layer);
              layer.destroy();
              layer = null;
            });
            layerSubscription.add(
              EsriUtils.load(layer)
                .pipe(
                  tap(() => {
                    groupLayer.add(layer);
                  }),
                  mergeMap(() => {
                    return from(this.mapView.whenLayerView(layer));
                  }),
                  finalize(() => {
                    this.loadingController.hide();
                  })
                )
                .subscribe()
            );
          } else {
            renderer = layer.renderer as any;
          }

          renderer.radius = e.distance;

          handleMainImageLayerVisible(true);
        } else {
          doDisable();
        }
      })
    );

    subscription.add(() => {
      doDisable();
    });

    this.mapViewSubscription = subscription;
  }
}
